import { useEffect, useRef } from "react";
import type { BoardToolId } from "../editor/types";
import {
  isDoubleTap,
  shouldRestoreTool,
  SPACE_GRACE_MS,
  SPACE_GRACE_TAIL_MS,
  SPACE_HOLD_MS,
  toolAfterDoubleTap,
} from "../editor/spaceGesture";
import { isTypingTarget } from "./useBoardToolShortcuts";

/**
 * Пробел: подержал — стрелка, отпустил — прежний инструмент. Двойной стук
 * переключает перо и ластик.
 *
 * Оба движения на одной клавише живут мирно: удержание засчитывается только
 * после SPACE_HOLD_MS, значит короткий стук инструмент не меняет и остаётся
 * стуком.
 *
 * Стрелка не исчезает в тот же миг, когда пробел отпущен: тачпад на ноутбуке
 * не слушается, пока нажата клавиша, и выделить удержанием там нечего было.
 * После отпускания стрелка ждёт SPACE_GRACE_MS, а если за это время успели
 * тронуть доску — ещё SPACE_GRACE_TAIL_MS после каждого действия, чтобы
 * выделенное можно было сразу подвинуть.
 */
export function useSpaceGesture(opts: {
  enabled: boolean;
  activeTool: BoardToolId;
  onToolChange: (tool: BoardToolId) => void;
}) {
  const { enabled, activeTool, onToolChange } = opts;

  const toolRef = useRef(activeTool);
  toolRef.current = activeTool;
  const changeRef = useRef(onToolChange);
  changeRef.current = onToolChange;

  const pressedAtRef = useRef<number | null>(null);
  const heldToolRef = useRef<BoardToolId | null>(null);
  const lastTapRef = useRef<number | null>(null);
  const holdTimerRef = useRef<number | null>(null);
  const graceTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    function clearHoldTimer() {
      if (holdTimerRef.current !== null) {
        window.clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
    }

    function clearGraceTimer() {
      if (graceTimerRef.current !== null) {
        window.clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
      }
    }

    /** Вернуть инструмент, который был до удержания. */
    function restoreTool() {
      const back = heldToolRef.current;
      heldToolRef.current = null;
      clearGraceTimer();
      if (!back) return;
      // Если за это время сами взяли другой инструмент — не отбираем.
      if (!shouldRestoreTool(toolRef.current)) return;
      changeRef.current(back);
    }

    /** Подождать ещё и только потом вернуть перо. */
    function armGrace(ms: number) {
      clearGraceTimer();
      if (!heldToolRef.current) return;
      graceTimerRef.current = window.setTimeout(() => {
        graceTimerRef.current = null;
        restoreTool();
      }, ms);
    }

    function onDown(ev: KeyboardEvent) {
      if (ev.code !== "Space") return;
      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (isTypingTarget(ev.target)) return;
      // Глушим КАЖДОЕ нажатие, включая автоповтор. Пока глушилось только
      // первое, браузер всё время удержания прокручивал страницу и «нажимал»
      // кнопку, на которой остался фокус: доска уезжала из-под курсора,
      // выделить что-либо было нельзя, а сам курсор двигался рывками.
      ev.preventDefault();
      ev.stopPropagation();
      if (ev.repeat) return;
      if (pressedAtRef.current !== null) return;
      // Фокус мог остаться на кнопке панели — тогда пробел её нажимает.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body) active.blur();
      pressedAtRef.current = Date.now();
      // Нажали снова, пока стрелка ещё в руке: прежний инструмент помним
      // дальше, а отсчёт возврата начинаем заново.
      clearGraceTimer();
      clearHoldTimer();
      holdTimerRef.current = window.setTimeout(() => {
        holdTimerRef.current = null;
        if (pressedAtRef.current === null) return;
        if (toolRef.current === "select") return;
        heldToolRef.current = toolRef.current;
        changeRef.current("select");
      }, SPACE_HOLD_MS);
    }

    function onUp(ev: KeyboardEvent) {
      if (ev.code !== "Space") return;
      ev.preventDefault();
      const pressedAt = pressedAtRef.current;
      pressedAtRef.current = null;
      clearHoldTimer();

      if (heldToolRef.current) {
        // Это было удержание: стуком оно не считается. Стрелка остаётся
        // в руке — на тачпаде только теперь ею и можно что-то сделать.
        lastTapRef.current = null;
        armGrace(SPACE_GRACE_MS);
        return;
      }
      if (pressedAt === null) return;

      const now = Date.now();
      if (isDoubleTap(lastTapRef.current, now)) {
        lastTapRef.current = null;
        const next = toolAfterDoubleTap(toolRef.current);
        if (next) changeRef.current(next);
        return;
      }
      lastTapRef.current = now;
    }

    /** Тронули доску, пока стрелка одолжена: торопиться с возвратом некуда. */
    function onPointerDown() {
      if (!heldToolRef.current) return;
      clearGraceTimer();
    }

    /** Действие закончено — даём короткий запас на следующее. */
    function onPointerUp() {
      if (!heldToolRef.current) return;
      armGrace(SPACE_GRACE_TAIL_MS);
    }

    /** Ушли из окна с зажатым пробелом — инструмент не должен залипнуть. */
    function onBlur() {
      pressedAtRef.current = null;
      lastTapRef.current = null;
      clearHoldTimer();
      restoreTool();
    }

    window.addEventListener("keydown", onDown, true);
    window.addEventListener("keyup", onUp, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", onPointerUp, true);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onDown, true);
      window.removeEventListener("keyup", onUp, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("blur", onBlur);
      clearHoldTimer();
      clearGraceTimer();
    };
  }, [enabled]);
}
